"use client";

import { useState } from "react";
import {
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure } from "@/components/tool-kit/charts/figure";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { PedigreeDiagram } from "@/components/tools/ecology/pedigree-diagram";
import type { KeyLabels } from "@/components/tools/ecology/pedigree-key";
import type { CarrierStyle } from "@/components/tools/ecology/pedigree-symbols";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { MAX_PEOPLE, parsePedigree } from "@/lib/tool-kit/ecology/pedigree";
import {
  layoutPedigree,
  type PedigreeLayout,
  type PlacedPerson,
} from "@/lib/tool-kit/ecology/pedigree-layout";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

/** English strings. A translated content file overrides these through `ui`. */
export const PEDIGREE_CHART_MAKER_STRINGS = {
  outlineLabel:
    "Your family — one person per line, children indented under their parents",
  outlineHint:
    "Write each person as Name (M or F, status). Join partners with +, or = for a consanguineous union, and indent children under their parents. Statuses: affected, carrier, presymptomatic, deceased or d. 2007, proband, consultand, adopted. Add b. 1980, age 45 or a “quoted note” for the label, mz or dz for twins, and pregnancy, miscarriage, termination or stillbirth.",
  exampleOutline:
    'Walter (M, carrier, d. 1998) + Edith (F, carrier, b. 1925)\n  Harold (M, carrier, b. 1950) + Joan (F, carrier, b. 1952)\n    Peter (M, affected, proband, dz, b. 1980)\n    Anne (F, carrier, dz, b. 1980)\n    Ruth (F, b. 1983, "unaffected, not tested")\n  Margaret (F, carrier, b. 1953) + Frank (M, affected, b. 1951)\n    Lucy (F, affected, b. 1978)\n    Tom (M, carrier, b. 1981)',
  conditionLabel: "Condition the pedigree is about (for the key)",
  conditionDefault: "phenylketonuria",
  carrierLabel: "Carrier symbol",
  carrierDot: "A dot in the symbol (NSGC 2008)",
  carrierHalf: "Half of the symbol filled",
  showNumerals: "Show generation numbers (I, II, III)",
  showNumbers: "Show a pedigree number under each person (II-3)",
  showNames: "Show names under the symbols",
  people: "Individuals",
  generations: "Generations",
  affectedCount: "Affected",
  carrierCount: "Carriers",
  unknownWords: "These words were not recognized and were ignored: {words}",
  lineWord: "line {line}: {word}",
  tooMany:
    "A pedigree here holds up to {limit} people; this outline has {count}.",
  enterOutline:
    "Type at least one person to start the pedigree, for example Anna (F, affected).",
  keyHeading: "Key",
  keyMale: "Male",
  keyFemale: "Female",
  keyUnknown: "Sex not specified",
  keyAffected: "Affected",
  keyAffectedWith: "Affected with {condition}",
  keyCarrier: "Carrier, not expected to develop the condition",
  keyPresymptomatic: "Carrier who may develop the condition",
  keyDeceased: "Deceased",
  keyProband: "Proband (P)",
  keyConsultand: "Consultand",
  keyConsanguineous: "Consanguineous partners",
  keySeparated: "Partners separated",
  keyAdopted: "Adopted (brackets)",
  keyMz: "Monozygotic twins",
  keyDz: "Dizygotic twins",
  keyPregnancy: "Pregnancy (P)",
  keyMiscarriage: "Spontaneous abortion (SAB)",
  keyTermination: "Termination of pregnancy (TOP)",
  keyStillbirth: "Stillbirth (SB)",
  letterProband: "P",
  letterPregnancy: "P",
  abbrSab: "SAB",
  abbrTop: "TOP",
  abbrSb: "SB",
  columnNumber: "Number",
  columnName: "Name",
  columnSex: "Sex",
  columnStatus: "Status",
  columnParents: "Parents",
  statusUnaffected: "unaffected",
  statusAffected: "affected",
  statusCarrier: "carrier",
  statusPresymptomatic: "presymptomatic carrier",
  statusDeceased: "deceased",
  statusProband: "proband",
  unnamed: "(no name)",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Title",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  colorLabel: "Fill color for affected individuals",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy pedigree text",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  showDataTable: "Show the pedigree as a table",
  chartLabel: "Pedigree chart",
  chartDescription: "Pedigree chart of {people} in {generations}. {list}.",
  peopleCount: "one:{count} individual|other:{count} individuals",
  generationsCount: "one:{count} generation|other:{count} generations",
  describeEntry: "{number} {name}",
} as const;

type Strings = typeof PEDIGREE_CHART_MAKER_STRINGS;

const ID = "pcm";

function keyLabels(strings: Strings, condition: string): KeyLabels {
  return {
    heading: strings.keyHeading,
    male: strings.keyMale,
    female: strings.keyFemale,
    unknown: strings.keyUnknown,
    affected:
      condition.trim() === ""
        ? strings.keyAffected
        : t(strings.keyAffectedWith, { condition: condition.trim() }),
    carrier: strings.keyCarrier,
    presymptomatic: strings.keyPresymptomatic,
    deceased: strings.keyDeceased,
    proband: strings.keyProband,
    consultand: strings.keyConsultand,
    consanguineous: strings.keyConsanguineous,
    separated: strings.keySeparated,
    adopted: strings.keyAdopted,
    mz: strings.keyMz,
    dz: strings.keyDz,
    pregnancy: strings.keyPregnancy,
    miscarriage: strings.keyMiscarriage,
    termination: strings.keyTermination,
    stillbirth: strings.keyStillbirth,
    probandLetter: strings.letterProband,
    pregnancyLetter: strings.letterPregnancy,
  };
}

function statusText(placed: PlacedPerson, strings: Strings): string {
  const { person } = placed;
  const base = {
    unaffected: strings.statusUnaffected,
    affected: strings.statusAffected,
    carrier: strings.statusCarrier,
    presymptomatic: strings.statusPresymptomatic,
  }[person.status];
  return [
    base,
    person.deceased ? strings.statusDeceased : "",
    person.proband ? strings.statusProband : "",
  ]
    .filter(Boolean)
    .join(", ");
}

/** Rows for the accessible table: who, what and whose child. */
function tableRows(layout: PedigreeLayout, strings: Strings): string[][] {
  const parentsOf = new Map<PlacedPerson, string>();
  for (const family of layout.families) {
    const names = [family.left, family.right]
      .filter((p): p is PlacedPerson => p !== null)
      .map((p) => p.person.name || p.number)
      .join(" + ");
    for (const child of family.children) {
      parentsOf.set(child.placed, names);
    }
  }
  const sexes = {
    m: strings.keyMale,
    f: strings.keyFemale,
    u: strings.keyUnknown,
  };
  return [...layout.people]
    .sort((a, b) => a.generation - b.generation || a.x - b.x)
    .map((placed) => [
      placed.number,
      placed.person.name || strings.unnamed,
      sexes[placed.person.sex],
      statusText(placed, strings),
      parentsOf.get(placed) ?? "",
    ]);
}

export default function PedigreeChartMakerWidget() {
  const strings = useToolUi(PEDIGREE_CHART_MAKER_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [raw, setRaw] = useState<string>(strings.exampleOutline);
  const [condition, setCondition] = useState<string>(strings.conditionDefault);
  const [carrier, setCarrier] = useState<CarrierStyle>("dot");
  const [showNumerals, setShowNumerals] = useState(true);
  const [showNumbers, setShowNumbers] = useState(true);
  const [showNames, setShowNames] = useState(true);
  const [style, update] = useChartStyle({
    title: "",
    xTitle: "",
    yTitle: "",
    color: "black",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });

  const parsed = parsePedigree(raw);
  const layout =
    parsed.count > 0 && !parsed.tooMany
      ? layoutPedigree(parsed.roots, {
          showNames,
          abbreviations: {
            miscarriage: strings.abbrSab,
            termination: strings.abbrTop,
            stillbirth: strings.abbrSb,
          },
        })
      : null;
  const affected = layout
    ? layout.people.filter((p) => p.person.status === "affected").length
    : 0;
  const carriers = layout
    ? layout.people.filter(
        (p) =>
          p.person.status === "carrier" || p.person.status === "presymptomatic"
      ).length
    : 0;
  const title = style.title.trim();
  const labels = keyLabels(strings, condition);

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.outlineHint}
        id={`${ID}-outline`}
        label={strings.outlineLabel}
        onChange={setRaw}
        rows={10}
        value={raw}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id={`${ID}-condition`}
          label={strings.conditionLabel}
          onChange={setCondition}
          value={condition}
        />
        <PillGroup
          label={strings.carrierLabel}
          name={`${ID}-carrier`}
          onChange={setCarrier}
          options={[
            { value: "dot", label: strings.carrierDot },
            { value: "half", label: strings.carrierHalf },
          ]}
          value={carrier}
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Checkbox
          checked={showNumerals}
          label={strings.showNumerals}
          onChange={setShowNumerals}
        />
        <Checkbox
          checked={showNumbers}
          label={strings.showNumbers}
          onChange={setShowNumbers}
        />
        <Checkbox
          checked={showNames}
          label={strings.showNames}
          onChange={setShowNames}
        />
      </div>
      <div aria-live="polite" className="space-y-2">
        {parsed.count === 0 ? <Notice>{strings.enterOutline}</Notice> : null}
        {parsed.tooMany ? (
          <ErrorNotice>
            {t(strings.tooMany, {
              limit: fmt(MAX_PEOPLE),
              count: fmt(parsed.count),
            })}
          </ErrorNotice>
        ) : null}
        {parsed.unknown.length > 0 ? (
          <Notice>
            {t(strings.unknownWords, {
              words: parsed.unknown
                .map((item) =>
                  t(strings.lineWord, {
                    line: fmt(item.line),
                    word: item.word,
                  })
                )
                .join("; "),
            })}
          </Notice>
        ) : null}
        {layout ? (
          <StatGrid>
            <StatCard
              label={strings.people}
              value={fmt(layout.people.length)}
            />
            <StatCard
              label={strings.generations}
              value={fmt(layout.generations)}
            />
            <StatCard label={strings.affectedCount} value={fmt(affected)} />
            <StatCard label={strings.carrierCount} value={fmt(carriers)} />
          </StatGrid>
        ) : null}
      </div>
      {layout ? (
        <ChartFigure
          apa={style.apa}
          data={raw.trim()}
          figureNumber={style.figureNumber}
          filename={chartFilename(style.title, "pedigree-chart")}
          labels={strings}
          note={style.note}
          table={{
            head: [
              strings.columnNumber,
              strings.columnName,
              strings.columnSex,
              strings.columnStatus,
              strings.columnParents,
            ],
            rows: tableRows(layout, strings),
          }}
          title={style.title}
        >
          <PedigreeDiagram
            description={t(strings.chartDescription, {
              people: tPlural(
                strings.peopleCount,
                locale,
                layout.people.length,
                { count: fmt(layout.people.length) }
              ),
              generations: tPlural(
                strings.generationsCount,
                locale,
                layout.generations,
                { count: fmt(layout.generations) }
              ),
              list: [...layout.people]
                .sort((a, b) => a.generation - b.generation || a.x - b.x)
                .map((p) =>
                  t(strings.describeEntry, {
                    number: p.number,
                    name: p.person.name,
                  }).trim()
                )
                .join(", "),
            })}
            glyphLabels={{
              pregnancy: strings.letterPregnancy,
              proband: strings.letterProband,
            }}
            id={`${ID}-svg`}
            keyLabels={labels}
            label={title || strings.chartLabel}
            layout={layout}
            options={{
              paint: {
                fill: CHART_COLORS[colorIndex(style.color)],
                carrier,
              },
              showNumerals,
              showNumbers,
            }}
            title={style.apa ? "" : title}
          />
        </ChartFigure>
      ) : null}
      <ChartStyleFields
        colorLabels={strings}
        idPrefix={ID}
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
